import { useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Save, Trash2 } from 'lucide-react';
import { Button, Card, Input, Textarea } from '@/components/ui';
import { ApiError } from '@/lib/api';
import { createMatter, deleteMatter, getMatter, updateMatter } from '@/lib/api-client';
import type { MatterDetail, MatterInput } from '@/lib/types';
import { useMatters } from '@/contexts/MatterContext';

interface FormState {
  name: string;
  anchorPoint: string;
  court: string;
  reference: string;
  context: string;
  dropboxPath: string;
  sharepointPath: string;
  emailKeywords: string;
}

const EMPTY_FORM: FormState = {
  name: '',
  anchorPoint: '',
  court: '',
  reference: '',
  context: '',
  dropboxPath: '',
  sharepointPath: '',
  emailKeywords: '',
};

// P4 Section 4.4: empty-state quick-start templates. Prefill court + a context
// scaffold so a new user (e.g. John) is not staring at a blank form.
const TEMPLATES: Record<string, Partial<FormState>> = {
  court: {
    court: 'Supreme Court of Victoria',
    context:
      'PARTIES:\nPROCEEDING / REFERENCE:\nWHAT IS IN DISPUTE:\nKEY DATES:\nORDERS SOUGHT:\nCURRENT STAGE:',
  },
  afca: {
    court: 'Australian Financial Complaints Authority',
    context:
      'FINANCIAL FIRM:\nAFCA CASE REFERENCE:\nPRODUCT / FACILITY:\nWHAT WENT WRONG:\nRESOLUTION SOUGHT:\nTIMELINE OF CONTACT:',
  },
  family: {
    court: 'Federal Circuit and Family Court of Australia',
    context:
      'PARTIES:\nFILE NUMBER:\nCHILDREN / PROPERTY POOL:\nCURRENT ORDERS:\nWHAT IS IN DISPUTE:\nNEXT COURT DATE:',
  },
  property: {
    court: '',
    context:
      'PROPERTY / ADDRESS:\nPARTIES:\nNATURE OF DISPUTE (contract, PEXA, planning, caveat):\nKEY DATES:\nWHAT YOU WANT:',
  },
};

function fromMatter(m: MatterDetail): FormState {
  return {
    name: m.name,
    anchorPoint: m.anchorPoint ?? '',
    court: m.court ?? '',
    reference: m.reference ?? '',
    context: m.context,
    dropboxPath: m.dropboxPath ?? '',
    sharepointPath: m.sharepointPath ?? '',
    emailKeywords: m.emailKeywords ?? '',
  };
}

function toInput(form: FormState): MatterInput {
  return {
    name: form.name.trim(),
    anchorPoint: form.anchorPoint.trim() || null,
    court: form.court.trim() || null,
    reference: form.reference.trim() || null,
    context: form.context,
    dropboxPath: form.dropboxPath.trim() || null,
    sharepointPath: form.sharepointPath.trim() || null,
    emailKeywords: form.emailKeywords.trim() || null,
  };
}

export function MatterEditor() {
  const params = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refresh, setActiveMatterId } = useMatters();

  const isNew = !params.id || params.id === 'new';
  const matterId = isNew ? null : (params.id ?? null);
  const template = searchParams.get('template');

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!matterId) {
      const preset = template ? TEMPLATES[template] : undefined;
      setForm(preset ? { ...EMPTY_FORM, ...preset } : EMPTY_FORM);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getMatter(matterId)
      .then((matter) => {
        if (cancelled) {
          return;
        }
        setForm(fromMatter(matter));
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) {
          return;
        }
        if (err instanceof ApiError && err.status === 404) {
          setNotFound(true);
        } else {
          setError(err instanceof Error ? err.message : 'Could not load matter');
        }
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [matterId, template]);

  if (notFound) {
    return <Navigate to="/matters" replace />;
  }

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (saving) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = toInput(form);
      if (!payload.name) {
        setError('Matter name is required.');
        setSaving(false);
        return;
      }
      if (matterId) {
        await updateMatter(matterId, payload);
      } else {
        const created = await createMatter(payload);
        setActiveMatterId(created.id);
      }
      await refresh();
      navigate('/matters');
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Save failed';
      setError(message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!matterId || deleting) {
      return;
    }
    if (!confirmDelete) {
      setConfirmDelete(true);
      window.setTimeout(() => setConfirmDelete(false), 4000);
      return;
    }
    setDeleting(true);
    setError(null);
    try {
      await deleteMatter(matterId);
      await refresh();
      navigate('/matters');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
      setDeleting(false);
      setConfirmDelete(false);
    }
  }

  return (
    <main className="min-h-screen bg-stone-950 px-6 py-10">
      <div className="mx-auto max-w-3xl space-y-6">
        <Link
          to="/matters"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-stone-500 hover:text-amber-400"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to matters
        </Link>

        <header className="space-y-2">
          <p className="text-[10px] uppercase tracking-[0.3em] text-stone-500">
            {isNew ? 'New matter' : 'Edit matter'}
          </p>
          <h1 className="font-display text-3xl italic text-amber-500">
            {isNew ? 'New matter' : form.name || 'Edit matter'}
          </h1>
        </header>

        {error ? (
          <Card variant="default" padding="md" className="border-red-900/40 bg-red-950/10">
            <p className="text-sm text-red-300">{error}</p>
          </Card>
        ) : null}

        {loading ? (
          <Card variant="panel" padding="lg">
            <p className="text-sm text-stone-500">Loading...</p>
          </Card>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <Card variant="panel" padding="lg" className="space-y-4">
              <Field label="Name" required>
                <Input
                  value={form.name}
                  onChange={(e) => update('name', e.target.value)}
                  placeholder="e.g. Manda Capital Holdings v Troiano"
                  required
                  maxLength={200}
                />
              </Field>
              <Field label="Anchor point">
                <Textarea
                  value={form.anchorPoint}
                  onChange={(e) => update('anchorPoint', e.target.value)}
                  rows={2}
                  placeholder="The single point that wins this matter, in one or two sentences."
                  maxLength={2000}
                />
                <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-stone-600">
                  The one point that wins this matter. The tool leads every answer with this and
                  warns you when you drift.
                </p>
              </Field>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Court">
                  <Input
                    value={form.court}
                    onChange={(e) => update('court', e.target.value)}
                    placeholder="Supreme Court of Victoria"
                    maxLength={200}
                  />
                </Field>
                <Field label="Reference">
                  <Input
                    value={form.reference}
                    onChange={(e) => update('reference', e.target.value)}
                    placeholder="S ECI 2025 01363"
                    maxLength={200}
                  />
                </Field>
              </div>

              <Field label="Case context">
                <Textarea
                  value={form.context}
                  onChange={(e) => update('context', e.target.value)}
                  rows={10}
                  placeholder="The full case background. Parties, key dates, the orders sought, the substance of the dispute, any procedural posture. Claude reads this on every query."
                  maxLength={50_000}
                />
                <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-stone-600">
                  {form.context.length} / 50000 chars
                </p>
              </Field>
            </Card>

            <Card variant="accent" padding="lg" className="space-y-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-amber-400">
                  Live source connections
                </p>
                <p className="mt-1 text-sm text-stone-400">
                  Tell Claude where to look when it searches via MCP. Folder paths must match what
                  the operator sees in Dropbox or SharePoint.
                </p>
              </div>
              <Field label="Dropbox folder path">
                <Input
                  value={form.dropboxPath}
                  onChange={(e) => update('dropboxPath', e.target.value)}
                  placeholder="/Legal/Manda Capital"
                  maxLength={500}
                />
              </Field>
              <Field label="SharePoint or OneDrive folder path">
                <Input
                  value={form.sharepointPath}
                  onChange={(e) => update('sharepointPath', e.target.value)}
                  placeholder="https://..."
                  maxLength={500}
                />
              </Field>
              <Field label="Outlook search keywords">
                <Input
                  value={form.emailKeywords}
                  onChange={(e) => update('emailKeywords', e.target.value)}
                  placeholder="Manda Capital, S ECI 2025 01363, Patrick Mahoney"
                  maxLength={500}
                />
                <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-stone-600">
                  Comma-separated. Claude includes these in Outlook searches.
                </p>
              </Field>
            </Card>

            <div className="flex items-center justify-between gap-3">
              {matterId ? (
                <Button
                  type="button"
                  variant="ghost"
                  leftIcon={<Trash2 className="h-4 w-4" />}
                  disabled={deleting}
                  onClick={handleDelete}
                  className={confirmDelete ? 'text-red-400' : ''}
                >
                  {confirmDelete ? 'Confirm archive' : 'Archive matter'}
                </Button>
              ) : (
                <span />
              )}
              <Button
                type="submit"
                variant="primary"
                leftIcon={<Save className="h-4 w-4" />}
                disabled={saving}
              >
                {saving ? 'Saving' : matterId ? 'Save changes' : 'Create matter'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </main>
  );
}

interface FieldProps {
  label: string;
  children: React.ReactNode;
  required?: boolean;
}

function Field({ label, children, required }: FieldProps) {
  return (
    <label className="block space-y-1">
      <span className="block text-[10px] uppercase tracking-[0.2em] text-stone-500">
        {label}
        {required ? <span className="ml-1 text-amber-500">*</span> : null}
      </span>
      {children}
    </label>
  );
}
